// CyclesMapView — long-horizon halving cycles + log-power regression view.
//
// Standalone page mounted at /liquidity-map/cycles. Intentionally
// SEPARATE from HeatmapCanvas: different chart library (recharts SVG),
// different cadence (refreshed every 6h server-side), different
// audience (long-term investors, not intraday traders).
//
// Design priorities (per product brief):
//   1. Transparency: show BOTH the algorithm-derived cycle top (weekly
//      close) AND the curated raw-wick ATH side by side when both
//      exist — never collapse them.
//   2. Methodology visible: a non-hover note explains how peaks and
//      bands are computed.
//   3. Halving countdown isolated: rendered in its own card with a
//      neutral disclaimer, NOT adjacent to any cycle/peak section.
//   4. Sample-size warning surfaced (not buried in tooltip) for assets
//      with <3 historical halvings (ZEC).
//   5. Neutral framing on the 4-year cycle debate (one explicit sentence).
//
// Coin support: only BTC + ZEC. Other Liquidity Map coins have no
// halving and appear disabled with a tooltip explaining why.
//
// i18n: full ES/EN/SK via /src/i18n/cycles.js.
import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, ArrowLeft, AlertTriangle, Clock, Camera } from "lucide-react";
import { toast } from "sonner";
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ReferenceDot,
  Customized,
  Brush,
  useXAxisDomain,
  useYAxisDomain,
  usePlotArea,
} from "recharts";

import { useLanguage } from "../../i18n/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import { tCycles } from "../../i18n/cycles";
import { tDca } from "../../i18n/dca";
import { API, safeJson } from "../../lib/api";
import { captureChartAsBlob, tryNativeShare } from "../../lib/captureChart";
import { PublishAnalysisModal } from "../PublishAnalysisModal";
import rebelWatermarkUrl from "../../assets/branding/rebel_watermark.png";
import {
  ChartShell,
  FsResponsiveContainer,
  FULLSCREEN_STYLES,
} from "./ChartShell";

// Coins supported by the cycles endpoint (must mirror HALVINGS map in
// the backend `cycles.py` — keep manually in sync, kept short on purpose).
const CYCLES_COINS = ["BTC", "ZEC"];
// Visual order shown in the selector (includes disabled siblings to make
// the "no halving" affordance visible — pedagogical reason).
const ALL_COINS = ["BTC", "ETH", "SOL", "BNB", "XRP", "ZEC"];

// Stable color palette for cycle overlay lines. Cycle index → color.
// Ordered by ascending recency so the ongoing cycle gets the most
// visually-distinct hue (amber) — matches the EN CURSO chip elsewhere.
const CYCLE_COLORS = ["#a78bfa", "#22d3ee", "#34d399", "#fbbf24"];

/**
 * Merge per-cycle event-relative series into a single recharts-friendly
 * data array. `series_key` is the field name on each point that holds
 * the numeric x value ("weeks_from_halving" or "weeks_from_peak").
 * `value_key` is the y field ("pct_change_from_halving_close" or
 * "pct_from_peak").
 *
 * The output is sorted by x ascending. When a given cycle has no point
 * at a given x, its key is OMITTED from that row → recharts renders
 * nothing there, which is the correct no-extrapolation behaviour
 * (especially for the ongoing cycle whose data ends early).
 */
function mergeOverlaySeries(cycles, series_key, value_key) {
  const xs = new Set();
  cycles.forEach((c) => {
    const arr = c[series_key] || [];
    arr.forEach((p) => xs.add(p[series_key === "echo_series" ? "weeks_from_halving" : "weeks_from_peak"]));
  });
  const sortedXs = Array.from(xs).sort((a, b) => a - b);
  return sortedXs.map((x) => {
    const row = { x };
    cycles.forEach((c) => {
      const xKey = series_key === "echo_series" ? "weeks_from_halving" : "weeks_from_peak";
      const pt = (c[series_key] || []).find((p) => p[xKey] === x);
      if (pt) row[`c${c.index}`] = pt[value_key];
      // else: leave undefined → recharts skips the point.
    });
    return row;
  });
}

/**
 * Renders one of the two event-relative overlay charts. Reused for
 * Echo and Drawdown — only the inputs differ.
 */
function EventOverlayChart({
  testid,
  title,
  subtitle,
  axisX,
  axisY,
  truncationNote,
  data,
  cycles,
  t,
  multiPeakLines,
}) {
  // Brush state per chart. Resets when the data array length changes
  // (i.e. when the user switches coin or the dataset is refreshed).
  const brush = useBrushRange(data ? data.length : 0);
  if (!data || data.length === 0) {
    return (
      <div
        data-testid={testid}
        className="mb-6 rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 text-sm text-zinc-400"
      >
        <div className="mb-1 text-sm font-semibold text-zinc-300">{title}</div>
        <p>{t.overlay_no_data}</p>
      </div>
    );
  }
  return (
    <ChartShell
      testId={testid}
      title={title}
      subtitle={subtitle}
      onReset={brush.reset}
      canReset={brush.isZoomed}
    >
      <FsResponsiveContainer width="100%" height={300}>
        <ComposedChart data={data} margin={{ top: 10, right: 16, left: 8, bottom: 20 }}>
          <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            type="number"
            domain={["dataMin", "dataMax"]}
            stroke="#71717a"
            tick={{ fill: "#a1a1aa", fontSize: 11 }}
            label={{ value: axisX, position: "bottom", offset: 0, fill: "#71717a", fontSize: 10 }}
          />
          <YAxis
            stroke="#71717a"
            tick={{ fill: "#a1a1aa", fontSize: 11 }}
            tickFormatter={(v) => `${v}%`}
            label={{ value: axisY, angle: -90, position: "insideLeft", fill: "#71717a", fontSize: 10, offset: 0 }}
          />
          <Tooltip
            contentStyle={{
              background: "rgba(8,5,8,0.95)",
              border: "1px solid #3f3f46",
              borderRadius: 6,
              fontSize: 11,
            }}
            labelStyle={{ color: "#a1a1aa" }}
            formatter={(value) => [`${value}%`, undefined]}
            labelFormatter={(x) => `${x} ${axisX.toLowerCase().split(" ").slice(-2).join(" ")}`}
          />
          {/* Reference line at y=0 makes the anchor visible */}
          <ReferenceLine y={0} stroke="#52525b" strokeDasharray="2 4" />
          {multiPeakLines && multiPeakLines.length > 0
            ? multiPeakLines.map((l) => {
                // One <Line> per (cycle, peak). Color from CYCLE_COLORS
                // by cycle_index so peers of the same cycle share a hue;
                // intermediate peaks rendered dashed to visually separate
                // 3a (dashed) from 3b (solid) of the same cycle.
                const colorIdx = (l.cycle_index - 1) % CYCLE_COLORS.length;
                const color = CYCLE_COLORS[colorIdx];
                const isIntermediate = l.label_code === "intermediate";
                const peakSuffix =
                  isIntermediate
                    ? t.peak_label_intermediate
                    : t.peak_label_final;
                const baseLabel = (l.is_ongoing
                  ? t.overlay_cycle_label_ongoing
                  : t.overlay_cycle_label
                ).replace("{n}", l.cycle_index);
                const name = (multiPeakLines.filter(
                  (x) => x.cycle_index === l.cycle_index
                ).length > 1)
                  ? `${baseLabel} · ${peakSuffix}`
                  : baseLabel;
                return (
                  <Line
                    key={l.key}
                    type="monotone"
                    dataKey={l.key}
                    stroke={color}
                    strokeWidth={l.is_ongoing ? 2 : 1.4}
                    strokeDasharray={isIntermediate ? "4 4" : "0"}
                    dot={false}
                    isAnimationActive={false}
                    name={name}
                    connectNulls={false}
                  />
                );
              })
            : cycles.map((c, idx) => {
                const color = CYCLE_COLORS[idx % CYCLE_COLORS.length];
                const labelTpl = c.is_ongoing
                  ? t.overlay_cycle_label_ongoing
                  : t.overlay_cycle_label;
                return (
                  <Line
                    key={c.index}
                    type="monotone"
                    dataKey={`c${c.index}`}
                    stroke={color}
                    strokeWidth={c.is_ongoing ? 2 : 1.4}
                    strokeDasharray={c.is_ongoing ? undefined : "0"}
                    dot={false}
                    isAnimationActive={false}
                    name={labelTpl.replace("{n}", c.index)}
                    connectNulls={false}
                  />
                );
              })}
          <Legend
            wrapperStyle={{ fontSize: 11, color: "#a1a1aa", paddingTop: 18 }}
            iconType="line"
          />
          <Brush
            dataKey="x"
            height={22}
            stroke="#3f3f46"
            fill="#18181b"
            travellerWidth={8}
            startIndex={brush.start}
            endIndex={brush.end}
            onChange={brush.setRange}
          />
        </ComposedChart>
      </FsResponsiveContainer>
      <p className="mt-2 text-[11px] italic text-zinc-500">{truncationNote}</p>
    </ChartShell>
  );
}

// ── Small UI primitives ────────────────────────────────────────────────
function Pill({ children, tone = "neutral", testId }) {
  const toneClass =
    tone === "warn"
      ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
      : tone === "success"
        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
        : tone === "info"
          ? "border-sky-500/40 bg-sky-500/10 text-sky-300"
          : "border-zinc-700 bg-zinc-900/60 text-zinc-300";
  return (
    <span
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${toneClass}`}
    >
      {children}
    </span>
  );
}

function formatPrice(v) {
  if (v == null || !isFinite(v)) return "—";
  if (v >= 1000) return `$${Math.round(v).toLocaleString("en-US")}`;
  if (v >= 1) return `$${v.toFixed(2)}`;
  return `$${v.toFixed(4)}`;
}

function formatDateISO(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toISOString().slice(0, 10);
}

function formatUpdated(t, ageS) {
  if (ageS == null || ageS < 60) return t.updated_just_now;
  if (ageS < 3600) return t.updated_minutes_ago.replace("{n}", Math.floor(ageS / 60));
  return t.updated_hours_ago.replace("{n}", Math.floor(ageS / 3600));
}

// Compact UTC stamp "YYYY-MM-DD HH:MM UTC" for the freshness pill. We
// show the exact moment the cached payload was computed so an ambiguous
// "3h ago" can never hide a doc that's actually pre-deploy.
function formatUtcStamp(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
  );
}

// Days remaining (UTC, integer). Negative if past.
function daysUntil(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (!isFinite(target)) return null;
  return Math.round((target - Date.now()) / 86_400_000);
}

// ── Fullscreen + ChartShell + brush hook ──────────────────────────
// `ChartShell`, `FsResponsiveContainer` and `FULLSCREEN_STYLES` are
// imported from ./ChartShell. The ResponsiveContainer + CSS pseudo-
// selector pattern lives there so both this view and the DCA simulator
// can reuse it without drifting.

// Hook: controlled Brush range for zoom-by-selection. Returns the
// pair of indices to feed into <Brush startIndex/endIndex>, a setter
// for the brush onChange, and a reset action.
function useBrushRange(dataLength) {
  const [range, setRange] = React.useState(null);
  const reset = React.useCallback(() => setRange(null), []);
  React.useEffect(() => {
    setRange(null);
  }, [dataLength]);
  const start = range?.startIndex ?? 0;
  const end = range?.endIndex ?? Math.max(0, dataLength - 1);
  const isZoomed = !!range && (range.startIndex > 0 || range.endIndex < dataLength - 1);
  return { start, end, setRange, reset, isZoomed };
}

// ── Chart annotations layer ─────────────────────────────────────────
// Global annotation layer rendered via Recharts <Customized>. Drives
// THREE behaviours that per-dot labels could not solve:
//   (1) Peak + Parabolic of the SAME peak fuse into one combined
//       placard (close+ATH lines, separator, parabolic line below).
//   (2) Pairwise overlap detection — any two placards that would
//       collide are stacked vertically (with a leader line back to
//       the anchor) so each remains attributable.
//   (3) Clamping — placards never spill past the plot area on the
//       left, right, or top.
//
// Each placard is just an SVG <g> with rounded <rect> + N <text>
// lines. Accent color per section means the close/ATH text remains
// cyan and the parabolic line stays blue even when both share a box.
const APPROX_CHAR_W = 5.4; // px at 9px monospace
const LINE_H = 11;
const PAD_X = 6;
const PAD_Y = 4;
const PLACARD_GAP = 6; // gap between leader-stacked placards
const LEADER_GAP = 4; // gap between dot and leader/box

function placardBox(lines) {
  const maxLen = Math.max(...lines.map((l) => String(l.text).length));
  const w = Math.max(maxLen * APPROX_CHAR_W + PAD_X * 2, 36);
  const h = lines.length * LINE_H + PAD_Y * 2 - 2;
  return { w, h };
}

function rectsOverlap(a, b) {
  return !(
    a.x + a.w < b.x ||
    b.x + b.w < a.x ||
    a.y + a.h < b.y ||
    b.y + b.h < a.y
  );
}

function AnnotationsLayer({ cycles, t }) {
  // Recharts 3.x: scales/plot area come from hooks, not props.
  // No `useScale` hook exists; we get domain + plot area and build
  // the mapping manually (x is linear time, y is log10).
  const xDomain = useXAxisDomain();
  const yDomain = useYAxisDomain();
  const plotArea = usePlotArea();
  if (!xDomain || !yDomain || !plotArea) return null;
  const xMin = Number(xDomain[0]);
  const xMax = Number(xDomain[1]);
  const yMin = Number(yDomain[0]);
  const yMax = Number(yDomain[1]);
  if (
    !isFinite(xMin) || !isFinite(xMax) || xMax <= xMin ||
    !isFinite(yMin) || !isFinite(yMax) || yMax <= yMin || yMin <= 0
  ) {
    return null;
  }
  const xScale = (v) =>
    plotArea.x + ((v - xMin) / (xMax - xMin)) * plotArea.width;
  const logMin = Math.log(yMin);
  const logMax = Math.log(yMax);
  const yScale = (v) =>
    v > 0
      ? plotArea.y +
        plotArea.height -
        ((Math.log(v) - logMin) / (logMax - logMin)) * plotArea.height
      : null;
  // Plot-area bounds in pixels.
  const left = plotArea.x;
  const top = plotArea.y;
  const right = plotArea.x + plotArea.width;
  const bottom = plotArea.y + plotArea.height;

  // 1. Build raw annotation specs. Peak+parabolic share one placard.
  // Spec shape: {key, anchorX, anchorY, preferTop, lines, strokeColor}
  const specs = [];
  cycles.forEach((c) => {
    const sps = c.significant_peaks || [];
    sps.forEach((sp) => {
      const closeDate = formatDateISO(sp.date);
      const hasWick =
        sp.known_ath_wick && sp.known_ath_wick.price !== sp.price;
      const wickDate = sp.known_ath_wick
        ? formatDateISO(sp.known_ath_wick.date)
        : null;
      // Compact format keeps placards narrow so they don't predictably
      // bump into each other at chart-zoom-out scale: "$X · date" per
      // line, no parentheses, no "(close ·" prefixes.
      const lines = [
        { text: `${formatPrice(sp.price)} · ${closeDate}`, color: "#67e8f9" },
      ];
      if (hasWick) {
        lines.push({
          text: `${formatPrice(sp.known_ath_wick.price)} ATH · ${
            wickDate || closeDate
          }`,
          color: "#67e8f9",
        });
      }
      // FIX 1: merge parabolic into the same placard as the peak it
      // belongs to. Divider rect splits sections so cyan vs blue text
      // remains attributable.
      if (sp.parabolic_start) {
        lines.push({ divider: true, text: "" });
        lines.push({
          text: `${t.legend_parabolic} · ${formatDateISO(
            sp.parabolic_start.date
          )}`,
          color: "#60a5fa",
        });
      }
      specs.push({
        key: `pk-${c.index}-${sp.peak_index}`,
        anchorX: xScale(Math.floor(new Date(sp.date).getTime() / 1000)),
        anchorY: yScale(sp.price),
        preferTop: true,
        lines,
        strokeColor: "#67e8f9",
      });
      // Bear trough (kept separate — far enough in time and on the
      // opposite y-direction that it never collides with the peak).
      if (sp.bear_market_trough) {
        specs.push({
          key: `bt-${c.index}-${sp.peak_index}`,
          anchorX: xScale(
            Math.floor(
              new Date(sp.bear_market_trough.date).getTime() / 1000
            )
          ),
          anchorY: yScale(sp.bear_market_trough.price),
          preferTop: false,
          lines: [
            {
              text: `${formatPrice(
                sp.bear_market_trough.price
              )} · ${formatDateISO(sp.bear_market_trough.date)}`,
              color: "#fda4af",
            },
          ],
          strokeColor: "#fda4af",
        });
      }
    });
  });

  // 2. Run a collision-avoidance pass: sort by anchorX, for each spec
  //    start at the preferred y; if it overlaps with any already-placed
  //    box, try a small set of alternative positions (above ↑, below ↓,
  //    nudge right →, nudge left ←). Bail out after a few attempts and
  //    accept the last tried position — better a slight overlap than a
  //    placard yanked across the whole chart.
  const placed = [];
  specs.sort((a, b) => a.anchorX - b.anchorX);
  specs.forEach((s) => {
    const { w, h } = placardBox(s.lines);
    const clampCx = (v) => {
      let cx = v;
      if (cx - w / 2 < left + 2) cx = left + 2 + w / 2;
      if (cx + w / 2 > right - 2) cx = right - 2 - w / 2;
      return cx;
    };
    // Candidate positions tried in order. The first non-overlapping one
    // wins; if all overlap, the preferred one (idx 0) is used anyway.
    const baseTop = s.anchorY - LEADER_GAP - h;
    const baseBot = s.anchorY + LEADER_GAP;
    const flipped = s.preferTop ? baseBot : baseTop;
    const stackUp = baseTop - h - PLACARD_GAP;
    const stackDown = baseBot + h + PLACARD_GAP;
    const candidates = [
      { cx: clampCx(s.anchorX), y: s.preferTop ? baseTop : baseBot },
      { cx: clampCx(s.anchorX), y: flipped },
      { cx: clampCx(s.anchorX), y: s.preferTop ? stackUp : stackDown },
      // Nudge sideways: keep y at preferred level, shift x by w * 0.55
      { cx: clampCx(s.anchorX + w * 0.55), y: s.preferTop ? baseTop : baseBot },
      { cx: clampCx(s.anchorX - w * 0.55), y: s.preferTop ? baseTop : baseBot },
      // Bigger sideways nudge as final resort.
      { cx: clampCx(s.anchorX + w * 1.05), y: s.preferTop ? baseTop : baseBot },
      { cx: clampCx(s.anchorX - w * 1.05), y: s.preferTop ? baseTop : baseBot },
      // Vertical stack on the FLIPPED side too (e.g. preferred top but
      // we already tried above and stackUp; allow another row below).
      { cx: clampCx(s.anchorX), y: flipped + (s.preferTop ? h + PLACARD_GAP : -(h + PLACARD_GAP)) },
    ];
    let chosen = candidates[0];
    for (const c of candidates) {
      const box = { x: c.cx - w / 2, y: c.y, w, h };
      // Reject if out of plot area.
      if (box.y < top + 2 || box.y + h > bottom - 2) continue;
      const conflict = placed.find((p) => rectsOverlap(box, p.box));
      if (!conflict) {
        chosen = c;
        break;
      }
    }
    const finalBox = { x: chosen.cx - w / 2, y: chosen.y, w, h };
    placed.push({ spec: s, cx: chosen.cx, w, h, y: chosen.y, box: finalBox });
  });

  // 3. Render — leader line from anchor to box edge if displaced; rect
  //    + text lines; never paint outside the plot area.
  return (
    <g style={{ pointerEvents: "none" }}>
      {placed.map(({ spec, cx, w, h, y, box }) => {
        const onLeft = cx < spec.anchorX - 1 || cx > spec.anchorX + 1;
        const drawLeader =
          onLeft ||
          Math.abs(spec.anchorY - (spec.preferTop ? y + h : y)) > 20;
        const leaderEndY = spec.preferTop ? y + h : y;
        return (
          <g key={spec.key}>
            {drawLeader && (
              <line
                x1={spec.anchorX}
                y1={spec.anchorY}
                x2={cx}
                y2={leaderEndY}
                stroke={spec.strokeColor}
                strokeOpacity={0.45}
                strokeWidth={1}
                strokeDasharray="2 2"
              />
            )}
            <rect
              x={box.x}
              y={box.y}
              width={w}
              height={h}
              rx={4}
              ry={4}
              fill="#18181b"
              fillOpacity={0.92}
              stroke={spec.strokeColor}
              strokeOpacity={0.55}
              strokeWidth={1}
            />
            {spec.lines.map((line, i) =>
              line.divider ? (
                <line
                  key={i}
                  x1={box.x + PAD_X}
                  x2={box.x + w - PAD_X}
                  y1={box.y + PAD_Y + (i + 1) * LINE_H - LINE_H / 2 - 1}
                  y2={box.y + PAD_Y + (i + 1) * LINE_H - LINE_H / 2 - 1}
                  stroke="#3f3f46"
                  strokeWidth={0.7}
                />
              ) : (
                <text
                  key={i}
                  x={cx}
                  y={box.y + PAD_Y + (i + 1) * LINE_H - 3}
                  fill={line.color}
                  fontSize={9}
                  fontFamily="ui-monospace,SFMono-Regular,Menlo,monospace"
                  textAnchor="middle"
                >
                  {line.text}
                </text>
              )
            )}
          </g>
        );
      })}
    </g>
  );
}

// ── Custom tooltip — keeps the chart hover concise. ────────────────────
function CycleTooltip({ active, payload, label, t }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload || {};
  return (
    <div
      data-testid="cycles-chart-tooltip"
      className="rounded-md border border-zinc-700 bg-zinc-950/95 px-3 py-2 text-xs text-zinc-200 shadow-xl"
    >
      <div className="font-mono text-[10px] text-zinc-400">{formatDateISO(p.date || label)}</div>
      <div className="mt-1 space-y-0.5">
        <div>
          <span className="text-zinc-400">{t.legend_price}: </span>
          <span className="font-mono text-zinc-100">{formatPrice(p.close)}</span>
        </div>
        {p.regression != null && (
          <div>
            <span className="text-zinc-400">{t.legend_regression}: </span>
            <span className="font-mono text-zinc-300">{formatPrice(p.regression)}</span>
          </div>
        )}
        {p.upper_band != null && p.lower_band != null && (
          <div className="text-zinc-500">
            {formatPrice(p.lower_band)} … {formatPrice(p.upper_band)}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────
export default function CyclesMapView() {
  const { lang } = useLanguage();
  const t = tCycles(lang);
  const [searchParams, setSearchParams] = useSearchParams();

  // Pre-select coin from `?coin=` query when supported. Falls back to BTC.
  // Two-way sync: changing the coin via UI updates the URL so the view
  // is link-shareable.
  const initialCoin = useMemo(() => {
    const q = String(searchParams.get("coin") || "").toUpperCase();
    return CYCLES_COINS.includes(q) ? q : "BTC";
  }, []);
  const [coin, setCoin] = useState(initialCoin);

  // Push coin changes back into the URL (replace, not push, so the back
  // button still takes the user to /liquidity-map cleanly).
  useEffect(() => {
    const current = String(searchParams.get("coin") || "").toUpperCase();
    if (current !== coin) {
      const next = new URLSearchParams(searchParams);
      next.set("coin", coin);
      setSearchParams(next, { replace: true });
    }
  }, [coin]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  // Bumped on retry to force a re-fetch even when `coin` did not change.
  const [reloadTick, setReloadTick] = useState(0);

  // ── B-Shadow share state — captures the share-root region and opens
  //    the existing PublishAnalysisModal pre-filled with cycle context.
  //    Same component/modal that LiqMapPage already uses, so styling,
  //    PRO gating, char-limit logic, etc. stay consistent.
  const { user, openLogin } = useAuth();
  const [shadowOpenSignal, setShadowOpenSignal] = useState(0);
  const [shadowPrefill, setShadowPrefill] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API}/liqmap/cycles/${coin}`)
      .then(async (r) => {
        const body = await safeJson(r);
        if (!r.ok) throw new Error((body && body.detail) || `HTTP ${r.status}`);
        if (!cancelled) setData(body);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message || String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [coin, reloadTick]);

  // Memoised chart-friendly slices so we don't recompute on every render.
  const chartData = useMemo(() => {
    if (!data?.weekly?.length) return [];
    // recharts wants a numeric x for log scale; we use epoch-seconds.
    return data.weekly.map((w) => ({
      x: w.ts,
      date: w.date,
      close: w.close,
      regression: w.regression,
      upper_band: w.upper_band,
      lower_band: w.lower_band,
      // band area helper: stack lower_band with band_width (upper-lower)
      // to draw a shaded vertical band via two stacked <Area>s.
      band_floor: w.lower_band,
      band_height:
        w.upper_band != null && w.lower_band != null
          ? Math.max(w.upper_band - w.lower_band, 0)
          : null,
    }));
  }, [data]);

  // Zoom-by-selection state for each of the 3 charts. Brush gives us
  // controlled ranges + reset; the hook also resets on data-length
  // change so navigating coins doesn't leave the brush stuck at indices
  // outside the new dataset.
  const priceBrush = useBrushRange(chartData.length);

  // Y-axis log domain — guarded against zero/negative data.
  const yDomain = useMemo(() => {
    if (!chartData.length) return ["auto", "auto"];
    const lows = chartData
      .map((d) => d.lower_band ?? d.close)
      .filter((v) => v != null && v > 0);
    const highs = chartData
      .map((d) => d.upper_band ?? d.close)
      .filter((v) => v != null && v > 0);
    if (!lows.length || !highs.length) return ["auto", "auto"];
    const lo = Math.min(...lows) * 0.85;
    const hi = Math.max(...highs) * 1.15;
    return [lo, hi];
  }, [chartData]);

  const halvings = data?.halvings || [];
  const cycles = data?.cycles || [];
  const sampleWarning = data?.sample_warning; // { code, sample_size } | null

  const nextHalvingDays = daysUntil(data?.next_halving_estimate);

  // ── Share-to-Shadow handler. Captures the `cycles-share-root` region
  //    (caveats panel + price chart + cycle cards) using the shared
  //    captureChart helper. Auto-fills the modal with cycle-relevant
  //    tags + a description that REUSES the same disclaimer texts from
  //    the caveats panel — so honesty travels with the post.
  //
  //    Tag semantics (intentionally minimal vs LiqMap's share):
  //      ticker     = current coin (BTC | ZEC)
  //      timeframe  = "macro/weekly"  (no intraday TF applies to a cycles view)
  //      direction  = ""              (NEUTRAL — this is reference data,
  //                                    not a trade signal; do NOT force a
  //                                    directional tag where none belongs)
  const onShareToShadow = async () => {
    if (!user) {
      try { openLogin && openLogin(); } catch { /* ignore */ }
      return;
    }
    const target = typeof document !== "undefined"
      ? document.querySelector('[data-testid="cycles-share-root"]')
      : null;
    if (!target) {
      toast.error(t.share_capture_failed || "No se pudo capturar el chart");
      return;
    }
    const blob = await captureChartAsBlob({
      target,
      watermarkUrl: rebelWatermarkUrl,
    });
    if (!blob) {
      toast.error(t.share_capture_failed || "No se pudo capturar el chart");
      return;
    }
    // Build a description that includes a short factual line + the
    // SAME disclaimer copy used in the caveats panel above. We pull
    // strings from `t.*` so they auto-translate per active language and
    // we never duplicate copy.
    const ongoing = cycles.find((c) => c.is_ongoing);
    const lines = [`📈 ${t.title} · ${coin}`];
    if (ongoing && ongoing.peak) {
      const dd = ongoing.stats?.drawdown_pct_peak_to_next_trough;
      const peakLine = `${t.cycle_peak_close}: $${Math.round(ongoing.peak.price).toLocaleString("en-US")}` +
        (ongoing.known_ath_wick != null
          ? ` · ${t.cycle_known_ath}: $${Math.round(ongoing.known_ath_wick).toLocaleString("en-US")}`
          : "");
      lines.push(peakLine);
      if (dd != null) lines.push(`${t.stats_col_drawdown}: ${dd}%`);
    }
    lines.push("");
    // Reuse the disclaimer copy verbatim — the same strings rendered
    // by the on-page caveats panel. Honesty travels with the post.
    if (sampleWarning && sampleWarning.code === "low_sample_size") {
      lines.push(
        `⚠️ ${t.sample_warning_title}: ${t.sample_warning_low_sample_size.replace("{n}", sampleWarning.sample_size)}`
      );
    }
    lines.push(`ℹ️ ${t.cycle_debate_title}: ${t.cycle_debate_body}`);
    lines.push(`📐 ${t.methodology_title}: ${t.methodology_body}`);
    setShadowPrefill({
      image: blob,
      title: "",
      description: lines.join("\n"),
      ticker: coin,
      timeframe: "macro/weekly",
      direction: "",
    });
    setShadowOpenSignal((v) => v + 1);
    tryNativeShare(blob, { title: `${coin} · ${t.title}` });
  };

  return (
    <div
      data-testid="cycles-map-view"
      className="min-h-screen bg-gradient-to-br from-zinc-950 via-black to-zinc-950 text-zinc-100"
    >
      <style>{FULLSCREEN_STYLES}</style>
      <div className="mx-auto max-w-6xl px-4 py-8">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Link
              to="/liquidity-map"
              data-testid="cycles-back-link"
              className="mb-2 inline-flex items-center gap-1.5 text-sm text-zinc-400 transition hover:text-amber-400"
            >
              <ArrowLeft className="h-4 w-4" />
              {t.back_to_liqmap}
            </Link>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">{t.title}</h1>
            <p className="mt-1 text-sm text-zinc-400">{t.subtitle}</p>
            {/* Cross-link to DCA Simulator — sister long-horizon view.
                Coin-agnostic (DCA supports BTC/ETH/SOL/XRP/ZEC), so
                always rendered enabled. */}
            <Link
              to={`/liquidity-map/dca?coin=${encodeURIComponent(String(coin || "BTC").toUpperCase())}`}
              data-testid="cycles-open-dca-link"
              title={tDca(lang).entry_button_title}
              className="mt-2 inline-flex items-center gap-1 rounded-md border border-emerald-400/55 bg-zinc-900 px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-emerald-300 transition hover:bg-emerald-400/10"
            >
              {tDca(lang).entry_button_label}
            </Link>
          </div>

          {/* Coin selector */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-zinc-500">
              {t.coin_selector_label}
            </span>
            {ALL_COINS.map((c) => {
              const supported = CYCLES_COINS.includes(c);
              const isActive = supported && c === coin;
              return (
                <button
                  key={c}
                  type="button"
                  disabled={!supported}
                  onClick={() => supported && setCoin(c)}
                  title={!supported ? t.coin_disabled_tooltip : undefined}
                  data-testid={`cycles-coin-${c.toLowerCase()}`}
                  className={`rounded-md border px-3 py-1.5 text-sm font-medium transition ${
                    isActive
                      ? "border-amber-400 bg-amber-400/10 text-amber-300"
                      : supported
                        ? "border-zinc-700 bg-zinc-900/60 text-zinc-300 hover:border-zinc-500 hover:text-zinc-100"
                        : "cursor-not-allowed border-zinc-800 bg-zinc-900/30 text-zinc-600"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        {/* Source + freshness pills */}
        <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
          <Pill tone="neutral" testId="cycles-source">{t.source_label}</Pill>
          {data && (
            <Pill tone="info" testId="cycles-updated">
              <Clock className="h-3 w-3" />
              {formatUpdated(t, data.cache_age_s)}
              {data.cache_layer && (
                <span
                  className="text-[10px] uppercase opacity-70"
                  data-testid="cycles-cache-layer"
                  title={
                    data.cache_schema_version
                      ? `schema ${data.cache_schema_version}`
                      : undefined
                  }
                >
                  · {t[`cached_layer_${data.cache_layer}`] || data.cache_layer}
                </span>
              )}
              {data.cache_generated_at && (
                <span
                  className="text-[10px] tabular-nums opacity-60"
                  data-testid="cycles-cache-generated-at"
                >
                  · {formatUtcStamp(data.cache_generated_at)}
                </span>
              )}
            </Pill>
          )}
          {/* Share button — mirrors the LiqScoreWidget pattern but lives
              right next to the source/freshness chips so it's the LAST
              thing the user sees in the meta row before the analysis
              itself. Disabled until data is loaded. */}
          {data && (
            <button
              type="button"
              data-testid="cycles-share-to-shadow"
              title={t.share_button_title}
              aria-label={t.share_button_title}
              onClick={onShareToShadow}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-cyan-500/55 bg-cyan-500/5 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-wider text-cyan-300 transition hover:bg-cyan-500/15"
            >
              <Camera className="h-3 w-3" />
              {t.share_button_label}
            </button>
          )}
        </div>

        {/* Loading / error */}
        {loading && !data && (
          <div
            data-testid="cycles-loading"
            className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900/40 p-6 text-sm text-zinc-400"
          >
            <Loader2 className="h-4 w-4 animate-spin" />
            {t.loading}
          </div>
        )}
        {error && (
          <div
            data-testid="cycles-error"
            className="flex flex-col items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-300"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              {t.err_load}
            </div>
            <button
              type="button"
              data-testid="cycles-retry-btn"
              onClick={() => setReloadTick((n) => n + 1)}
              className="rounded-md border border-red-500/40 bg-red-500/20 px-3 py-1 text-xs font-medium text-red-200 transition hover:bg-red-500/30"
            >
              {t.retry}
            </button>
          </div>
        )}

        {/* ── Share-root: the section captured by the "Share to Shadow"
            flow. Wraps caveats + price chart + cycle cards so the
            honesty panel travels with the screenshot, not just the bare
            chart. Stats table + event-relative overlays + next-halving
            are intentionally EXCLUDED to keep the captured image
            focused and shareable on social. */}
        {data && (
          <div data-testid="cycles-share-root" className="relative">
        {/* ─── Consolidated honesty panel (replaces the 3 separate cards
            below — sample warning, methodology, cycle debate). Adds
            two NEW bullets the user requested: ±2σ band describes past
            only; parabolic detection uses a statistical criterion and
            may legitimately be absent.

            UX choice: `<details open>` so it's collapsible without being
            hover-gated (matches the "metodología visible sin hover"
            criterion already applied throughout C2). The bullet for the
            sample-warning is conditionally rendered (only when the
            backend says n<3 halvings, i.e. ZEC today). The parabolic
            bullet is always present because the FEATURE exists across
            the page regardless of whether THIS coin happened to trigger
            a detection in any specific cycle. */}
        {data && (
          <details
            data-testid="cycles-caveats-panel"
            open
            className="mb-6 rounded-lg border border-amber-500/30 bg-amber-500/[0.04] p-3 text-sm text-amber-100/90"
          >
            <summary className="flex cursor-pointer select-none items-center gap-2 font-semibold text-amber-200 [&::-webkit-details-marker]:hidden">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{t.caveats_panel_title}</span>
              <span className="ml-auto font-mono text-[10px] opacity-60">▾</span>
            </summary>
            <p className="mt-2 text-xs uppercase tracking-wider text-amber-200/70">
              {t.caveats_panel_intro}
            </p>
            <ul className="mt-2 list-disc space-y-2 pl-5 leading-relaxed text-amber-50/85">
              {sampleWarning && sampleWarning.code === "low_sample_size" && (
                <li data-testid="cycles-caveat-sample-size">
                  <strong className="text-amber-200">{t.sample_warning_title}.</strong>{" "}
                  {t.sample_warning_low_sample_size.replace("{n}", sampleWarning.sample_size)}
                </li>
              )}
              <li data-testid="cycles-caveat-4y-debate">
                <strong className="text-amber-200">{t.cycle_debate_title}.</strong>{" "}
                {t.cycle_debate_body}
              </li>
              <li data-testid="cycles-caveat-methodology">
                <strong className="text-amber-200">{t.methodology_title}.</strong>{" "}
                {t.methodology_body}
              </li>
              <li data-testid="cycles-caveat-band-past">
                <strong className="text-amber-200">{t.legend_band}.</strong>{" "}
                {t.caveats_band_past_only}
              </li>
              <li data-testid="cycles-caveat-parabolic">
                <strong className="text-amber-200">{t.legend_parabolic}.</strong>{" "}
                {t.caveats_parabolic_optional}
              </li>
            </ul>
          </details>
        )}

        {/* Chart */}
        {data && chartData.length > 0 && (
          <ChartShell
            testId="cycles-chart-wrapper"
            title={t.chart_title}
            onReset={priceBrush.reset}
            canReset={priceBrush.isZoomed}
          >
            <FsResponsiveContainer width="100%" height={420}>
              <ComposedChart data={chartData} margin={{ top: 32, right: 16, left: 8, bottom: 8 }}>
                <CartesianGrid stroke="#27272a" strokeDasharray="3 3" />
                <XAxis
                  dataKey="x"
                  type="number"
                  scale="time"
                  domain={["dataMin", "dataMax"]}
                  tickFormatter={(v) => new Date(v * 1000).getUTCFullYear()}
                  stroke="#71717a"
                  tick={{ fill: "#a1a1aa", fontSize: 11 }}
                />
                <YAxis
                  scale="log"
                  domain={yDomain}
                  tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)}
                  stroke="#71717a"
                  tick={{ fill: "#a1a1aa", fontSize: 11 }}
                  allowDataOverflow
                />
                <Tooltip content={<CycleTooltip t={t} />} />
                <Legend
                  wrapperStyle={{ fontSize: 11, color: "#a1a1aa", paddingTop: 8 }}
                  iconType="line"
                />

                {/* Band: lower as transparent floor + height as visible area */}
                <Area
                  type="monotone"
                  dataKey="band_floor"
                  stackId="band"
                  stroke="none"
                  fill="transparent"
                  isAnimationActive={false}
                  legendType="none"
                  name=""
                />
                <Area
                  type="monotone"
                  dataKey="band_height"
                  stackId="band"
                  stroke="none"
                  fill="#71717a"
                  fillOpacity={0.18}
                  isAnimationActive={false}
                  name={t.legend_band}
                />

                {/* Regression mean */}
                <Line
                  type="monotone"
                  dataKey="regression"
                  stroke="#a1a1aa"
                  strokeDasharray="5 4"
                  dot={false}
                  isAnimationActive={false}
                  name={t.legend_regression}
                />
                {/* Price */}
                <Line
                  type="monotone"
                  dataKey="close"
                  stroke="#fbbf24"
                  strokeWidth={1.5}
                  dot={false}
                  isAnimationActive={false}
                  name={t.legend_price}
                />

                {/* Halving vertical guides — H{idx} on top, YYYY-MM-DD just below. */}
                {halvings.map((h) => {
                  const x = Math.floor(new Date(h.date).getTime() / 1000);
                  return (
                    <ReferenceLine
                      key={`h-${h.index}`}
                      x={x}
                      stroke="#f97316"
                      strokeDasharray="2 4"
                      label={{
                        value: `H${h.index} · ${formatDateISO(h.date)}`,
                        position: "top",
                        fill: "#fb923c",
                        fontSize: 10,
                      }}
                    />
                  );
                })}

                {/* Peak / trough / parabolic — multi-peak aware. Renders
                    a labelled dot per significant_peak (so cycle 3 BTC
                    shows BOTH the nov-2021 and mar-2024 peaks) plus the
                    per-peak bear_market_trough (the actual market
                    bottom, not the post-halving anchor). */}
                {cycles.flatMap((c) => {
                  const out = [];
                  const sps = c.significant_peaks || [];
                  sps.forEach((sp) => {
                    out.push(
                      <ReferenceDot
                        key={`pk-${c.index}-${sp.peak_index}`}
                        x={Math.floor(new Date(sp.date).getTime() / 1000)}
                        y={sp.price}
                        r={5}
                        fill="#22d3ee"
                        stroke="#0e7490"
                        ifOverflow="extendDomain"
                      />
                    );
                    // Bear-market trough — the REAL post-peak bottom.
                    if (sp.bear_market_trough) {
                      out.push(
                        <ReferenceDot
                          key={`bt-${c.index}-${sp.peak_index}`}
                          x={Math.floor(new Date(sp.bear_market_trough.date).getTime() / 1000)}
                          y={sp.bear_market_trough.price}
                          r={4}
                          fill="#fb7185"
                          stroke="#9f1239"
                          ifOverflow="extendDomain"
                        />
                      );
                    }
                    if (sp.parabolic_start) {
                      out.push(
                        <ReferenceDot
                          key={`pa-${c.index}-${sp.peak_index}`}
                          x={Math.floor(new Date(sp.parabolic_start.date).getTime() / 1000)}
                          y={sp.parabolic_start.price}
                          r={4}
                          fill="#3b82f6"
                          stroke="#1d4ed8"
                          ifOverflow="extendDomain"
                        />
                      );
                    }
                  });
                  // Post-halving low marker (the rally anchor).
                  if (c.cycle_start_low) {
                    out.push(
                      <ReferenceDot
                        key={`sl-${c.index}`}
                        x={Math.floor(new Date(c.cycle_start_low.date).getTime() / 1000)}
                        y={c.cycle_start_low.price}
                        r={3}
                        fill="#a78bfa"
                        stroke="#5b21b6"
                      />
                    );
                  }
                  return out;
                })}
                {/* Global annotation layer: collision-aware placards
                    rendered AFTER all dots so they sit on top. Merges
                    peak + parabolic into one card, stacks overlapping
                    placards with leader lines, clamps to plot area. */}
                <Customized
                  // eslint-disable-next-line react/no-unstable-nested-components
                  component={() => (
                    <AnnotationsLayer cycles={cycles} t={t} />
                  )}
                />
                <Brush
                  dataKey="x"
                  height={22}
                  stroke="#3f3f46"
                  fill="#18181b"
                  travellerWidth={8}
                  startIndex={priceBrush.start}
                  endIndex={priceBrush.end}
                  onChange={priceBrush.setRange}
                  tickFormatter={(v) => new Date(v * 1000).getUTCFullYear()}
                />
              </ComposedChart>
            </FsResponsiveContainer>

            {/* Compact legend chips below */}
            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-zinc-400">
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2 w-3 rounded-sm bg-amber-400" />
                {t.legend_price}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-0.5 w-3 bg-zinc-400" style={{ borderTop: "1px dashed" }} />
                {t.legend_regression}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2 w-3 rounded-sm bg-zinc-400/30" />
                {t.legend_band}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2 w-0.5 bg-orange-500" />
                {t.legend_halving}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-cyan-400" />
                {t.legend_peak}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-violet-400" />
                {t.legend_trough}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-2 w-2 rounded-full bg-blue-500" />
                {t.legend_parabolic}
              </span>
            </div>
          </ChartShell>
        )}

        {/* Cycle cards — multi-peak aware. Each cycle renders ALL its
            significant_peaks (cycle 3 BTC has 2: nov-2021 + mar-2024).
            Each peak shows its own known_ath_wick {price, date} and
            bear_market_trough {price, date}. The post-halving low is
            shown as `cycle_start_low` (renamed from the misleading
            "Suelo de ciclo" — which actually meant the rally anchor,
            not the bear-market bottom). */}
        {data && cycles.length > 0 && (
          <div data-testid="cycles-cards" className="mb-8 grid gap-3 sm:grid-cols-2">
            {cycles.map((c) => {
              const sps = c.significant_peaks || [];
              const sLow = c.cycle_start_low;
              return (
                <div
                  key={c.index}
                  data-testid={`cycles-card-${c.index}`}
                  className={`rounded-xl border p-4 ${
                    c.is_ongoing
                      ? "border-amber-500/40 bg-amber-500/5"
                      : "border-zinc-800 bg-zinc-900/40"
                  }`}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-sm font-semibold text-zinc-200">
                      {t.cycle_label.replace("{n}", c.index)}
                    </div>
                    {c.is_ongoing && (
                      <Pill tone="warn" testId={`cycles-ongoing-${c.index}`}>
                        {t.cycle_ongoing}
                      </Pill>
                    )}
                  </div>

                  {/* Post-halving low (start of rally) */}
                  {sLow && (
                    <div
                      data-testid={`cycles-start-low-${c.index}`}
                      className="mb-3 rounded-md border border-zinc-800/60 bg-zinc-950/40 px-2 py-1.5"
                    >
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                        {t.cycle_start_low_label}
                      </div>
                      <div className="font-mono text-sm text-zinc-300">
                        {formatPrice(sLow.price)}{" "}
                        <span className="text-xs text-zinc-500">
                          {t.on_date.replace("{date}", formatDateISO(sLow.date))}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Per-peak blocks */}
                  {sps.length === 0 && (
                    <div className="text-xs italic text-zinc-500">{t.cycle_no_data}</div>
                  )}
                  {sps.map((sp) => {
                    const labelTpl =
                      sp.label_code === "sole"
                        ? null
                        : sp.label_code === "intermediate"
                          ? t.peak_label_intermediate
                          : t.peak_label_final;
                    return (
                      <div
                        key={sp.peak_index}
                        data-testid={`cycles-peak-${c.index}-${sp.peak_index}`}
                        className="mb-3 space-y-1 border-l-2 border-cyan-500/40 pl-3 last:mb-0"
                      >
                        {labelTpl && (
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-cyan-300/80">
                            {labelTpl}
                          </div>
                        )}
                        {/* Weekly-close peak */}
                        <div>
                          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-zinc-500">
                            <span>{t.cycle_peak_close}</span>
                            <span
                              title={t.cycle_peak_close_help}
                              data-testid={`cycles-peak-close-help-${c.index}-${sp.peak_index}`}
                              className="inline-flex h-3.5 w-3.5 cursor-help items-center justify-center rounded-full border border-zinc-700 text-[8px] font-bold text-zinc-500 hover:border-zinc-500 hover:text-zinc-300"
                              aria-label={t.cycle_peak_close_help}
                            >?</span>
                          </div>
                          <div className="font-mono text-base text-cyan-300">
                            {formatPrice(sp.price)}{" "}
                            <span className="text-xs text-zinc-500">
                              {t.on_date.replace("{date}", formatDateISO(sp.date))}
                            </span>
                          </div>
                        </div>
                        {/* Known ATH wick — both price AND date now */}
                        {sp.known_ath_wick && (
                          <div data-testid={`cycles-known-ath-${c.index}-${sp.peak_index}`}>
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                              {t.cycle_known_ath}
                            </div>
                            <div className="font-mono text-base text-amber-300">
                              {formatPrice(sp.known_ath_wick.price)}{" "}
                              <span className="text-xs text-zinc-500">
                                {t.on_date.replace(
                                  "{date}",
                                  formatDateISO(sp.known_ath_wick.date)
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                        {/* Bear-market trough (the REAL post-peak bottom) */}
                        {sp.bear_market_trough && (
                          <div data-testid={`cycles-bear-trough-${c.index}-${sp.peak_index}`}>
                            <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                              {t.cycle_bear_trough_label}
                            </div>
                            <div className="font-mono text-base text-rose-300">
                              {formatPrice(sp.bear_market_trough.price)}{" "}
                              <span className="text-xs text-zinc-500">
                                {t.on_date.replace(
                                  "{date}",
                                  formatDateISO(sp.bear_market_trough.date)
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
          </div>
        )}{/* /cycles-share-root */}

        {/* Per-cycle stats table — retrospective metadata, mandatory
            sample-size disclaimer in the heading. No projection logic. */}
        {data && cycles.length > 0 && (() => {
          const closed = cycles.filter((c) => !c.is_ongoing && c.peak && c.trough);
          const nClosed = closed.length;
          const disclaimer =
            nClosed === 1
              ? t.stats_disclaimer_one
              : t.stats_disclaimer_many.replace("{n}", nClosed);
          return (
            <div
              data-testid="cycles-stats-section"
              className="mb-8 rounded-xl border border-zinc-800 bg-zinc-950/60 p-4"
            >
              <div className="mb-2 text-sm font-semibold text-zinc-300">
                {t.stats_section_title}
              </div>
              <p
                data-testid="cycles-stats-disclaimer"
                className="mb-3 text-xs leading-relaxed text-amber-200/80"
              >
                {disclaimer}
              </p>
              <div className="overflow-x-auto">
                <table
                  data-testid="cycles-stats-table"
                  className="min-w-full text-xs text-zinc-300"
                >
                  <thead>
                    <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-wider text-zinc-500">
                      <th className="py-2 pr-4 text-left font-semibold">{t.stats_col_cycle}</th>
                      <th className="py-2 pr-4 text-left font-semibold">{t.stats_col_status}</th>
                      <th className="py-2 pr-4 text-right font-semibold">{t.stats_col_duration}</th>
                      <th className="py-2 pr-4 text-right font-semibold">{t.stats_col_gain}</th>
                      <th
                        className="py-2 pr-4 text-right font-semibold"
                        title={t.stats_col_gain_prev_bear_help}
                      >
                        {t.stats_col_gain_prev_bear}
                      </th>
                      <th className="py-2 pr-4 text-right font-semibold">{t.stats_col_halving_to_peak}</th>
                      <th className="py-2 text-right font-semibold">{t.stats_col_drawdown}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cycles.flatMap((c) => {
                      const sps = c.significant_peaks || [];
                      const rows = sps.length ? sps : [null];
                      return rows.map((sp, idx) => {
                        const s = (sp && sp.stats) || {};
                        const fmtW = (v) =>
                          v == null ? t.stats_value_dash : t.stats_value_weeks.replace("{n}", v);
                        const fmtPct = (v, withSign = false) => {
                          if (v == null) return t.stats_value_dash;
                          const sign = withSign && v > 0 ? "+" : "";
                          return t.stats_value_pct.replace("{n}", `${sign}${v}`);
                        };
                        // Drawdown: now uses per-peak bear_market_trough.
                        // Ongoing cycle with no successor → "Pendiente".
                        const ddPct = s.drawdown_pct_peak_to_bear_trough;
                        const drawdownCell =
                          ddPct != null
                            ? fmtPct(ddPct)
                            : c.is_ongoing
                              ? t.stats_value_pending
                              : t.stats_value_dash;
                        // Cycle label: when a cycle has multiple peaks,
                        // append the peak ordinal (e.g. "Ciclo 3 · Pico 1").
                        const cycleLbl = sps.length > 1
                          ? `${t.cycle_label.replace("{n}", c.index)} · ${
                              sp.label_code === "intermediate"
                                ? t.peak_label_intermediate
                                : t.peak_label_final
                            }`
                          : t.cycle_label.replace("{n}", c.index);
                        const rowKey = sp ? `${c.index}-${sp.peak_index}` : `${c.index}`;
                        return (
                          <tr
                            key={rowKey}
                            data-testid={`cycles-stats-row-${rowKey}`}
                            className="border-b border-zinc-900/60 last:border-0"
                          >
                            <td className="py-2 pr-4 font-mono text-zinc-200">{cycleLbl}</td>
                            <td className="py-2 pr-4">
                              {c.is_ongoing && idx === sps.length - 1 ? (
                                <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-300">
                                  {t.stats_status_ongoing}
                                </span>
                              ) : (
                                <span className="text-zinc-500">{t.stats_status_closed}</span>
                              )}
                            </td>
                            <td className="py-2 pr-4 text-right font-mono">
                              {fmtW(s.duration_start_low_to_peak_weeks)}
                            </td>
                            <td className="py-2 pr-4 text-right font-mono text-emerald-300">
                              {fmtPct(s.gain_pct_start_low_to_peak, true)}
                            </td>
                            <td
                              className="py-2 pr-4 text-right font-mono text-emerald-300/80"
                              data-testid={`cycles-stats-gain-prev-bear-${rowKey}`}
                            >
                              {fmtPct(s.gain_pct_prev_bear_to_peak, true)}
                            </td>
                            <td className="py-2 pr-4 text-right font-mono">
                              {fmtW(s.weeks_halving_to_peak)}
                            </td>
                            <td className="py-2 text-right font-mono text-rose-300">
                              {drawdownCell}
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Event-relative overlays — Echo (% since halving) + Drawdown (%
            since peak). Reindex existing weekly data onto event-aligned
            x axes so cycles can be compared directly without calendar-
            year confounders. NO extrapolation: the ongoing cycle's
            line just stops at the latest weekly close. */}
        {data && cycles.length > 0 && (() => {
          const echoData = mergeOverlaySeries(cycles, "echo_series", "pct_change_from_halving_close");
          // Drawdown overlay: now flatten per-peak series so cycle 3 BTC
          // contributes TWO lines (3a nov-2021 → bear / 3b mar-2024 →
          // halving). One line per (cycle, peak).
          const ddLines = cycles.flatMap((c) => {
            const arr = c.drawdown_series_per_peak || [];
            if (arr.length === 0) return [];
            return arr.map((pp) => ({
              key: `c${c.index}_p${pp.peak_index}`,
              cycle_index: c.index,
              peak_index: pp.peak_index,
              label_code: pp.label_code,
              series: pp.series,
              is_ongoing: c.is_ongoing,
            }));
          });
          // Merge into a single x-indexed array. Each line's points keyed
          // by `c{cycle_index}_p{peak_index}` so recharts can pull them
          // individually with connectNulls=false.
          const ddXs = new Set();
          ddLines.forEach((l) => l.series.forEach((p) => ddXs.add(p.weeks_from_peak)));
          const ddData = Array.from(ddXs).sort((a, b) => a - b).map((x) => {
            const row = { x };
            ddLines.forEach((l) => {
              const pt = l.series.find((p) => p.weeks_from_peak === x);
              if (pt) row[l.key] = pt.pct_from_peak;
            });
            return row;
          });
          return (
            <>
              <EventOverlayChart
                testid="cycles-echo-chart"
                title={t.echo_section_title}
                subtitle={t.echo_section_subtitle}
                axisX={t.echo_axis_x}
                axisY={t.overlay_axis_y_pct}
                truncationNote={t.overlay_truncation_note}
                data={echoData}
                cycles={cycles}
                t={t}
              />
              <EventOverlayChart
                testid="cycles-drawdown-chart"
                title={t.drawdown_section_title}
                subtitle={t.drawdown_section_subtitle}
                axisX={t.drawdown_axis_x}
                axisY={t.overlay_axis_y_pct}
                truncationNote={t.overlay_truncation_note}
                data={ddData}
                cycles={cycles}
                t={t}
                multiPeakLines={ddLines}
              />
            </>
          );
        })()}

        {/* Next halving countdown — INTENTIONALLY isolated at bottom, not
            adjacent to the cycle peaks. Neutral framing only. */}
        {data && data.next_halving_estimate && (
          <div
            data-testid="cycles-next-halving"
            className="rounded-xl border border-zinc-700/60 bg-zinc-950/60 p-4"
          >
            <div className="text-xs uppercase tracking-wider text-zinc-500">
              {t.next_halving_title}
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-2">
              <span className="font-mono text-2xl font-semibold text-zinc-100">
                {nextHalvingDays != null && nextHalvingDays > 0
                  ? t.next_halving_days.replace("{n}", nextHalvingDays.toLocaleString("en-US"))
                  : t.next_halving_passed}
              </span>
              <span className="font-mono text-xs text-zinc-500">
                ({formatDateISO(data.next_halving_estimate)})
              </span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-zinc-500">{t.next_halving_note}</p>
          </div>
        )}
      </div>

      {/* B-Shadow — reuses the SAME publish modal mounted by LiqMapPage.
          openSignal is bumped imperatively when the user clicks the
          share button; the modal stays unmounted-state until then. */}
      <PublishAnalysisModal
        openSignal={shadowOpenSignal}
        initialImage={shadowPrefill?.image || null}
        initialTitle={shadowPrefill?.title || ""}
        initialDescription={shadowPrefill?.description || ""}
        initialTicker={shadowPrefill?.ticker || ""}
        initialTimeframe={shadowPrefill?.timeframe || ""}
        initialDirection={shadowPrefill?.direction || ""}
      />
    </div>
  );
}
